import { useEffect, useState } from "react";
import { AuthenticatedUser } from "../../../interfaces/user.model";
import useUserInfo from "../../../hooks/useUserInfo";
import useAxiosInstance from "../../../hooks/useAxiosInstance";
import { API_RESULT, API_VERSION } from "../../../enums/common.enum";
import { amountConverter, handleError } from "../../../utils/common";
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { commonTableOptions } from "../../../constants";
import MUIDataTable from "mui-datatables";
import PermIdentityIcon from "@mui/icons-material/PermIdentity";
import UpdateApplicationModal from "./updateApplicationModal";

const BorrowerFinancing = () => {
  const privateInstance = useAxiosInstance();
  const [data, setData] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [updateApplication, setUpdateApplication] = useState(false);
  const [activeBorrowerId, setActiveBorrowerId] = useState<string>("");
  const [filterBy, setFilterBy] = useState("under_review");
  const user: AuthenticatedUser = useUserInfo();
  const [activeInvoiceUrl, setActiveInvoiceUrl] = useState<string>("");
  const [activeApplicationId, setActiveApplicationId] = useState<string>("");
  const [accountNumber, setAccountNumber] = useState<string>("");
  const [payableAmount, setPayableAmount] = useState<string>("");
  const [payeeName, setPayeeName] = useState<string>("");
  const [ifscCode, setIfscCode] = useState<string>("");

  useEffect(() => {
    const getData = async () => {
      if (data?.length > 0) {
        setData([]);
      }
      setLoading(true);
      try {
        const result = await privateInstance.get(
          `${API_VERSION.V3}/borrowers/applications?status=${filterBy}`,
        );
        if (result?.data?.status === API_RESULT.SUCCESS) {
          setData(result?.data?.response);
        }
      } catch (err) {
        handleError(err);
      } finally {
        setLoading(false);
      }
    };
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBy]);

  const cols: any = [
    {
      name: "invoice_id",
      label: "Invoice ID",
      options: {
        filter: false,
      },
    },
    {
      name: "payee_name",
      label: "Payee Name",
      options: {
        filter: false,
      },
    },
    {
      name: "invoice_date",
      label: "Invoice Date",
      options: { filter: false, display: false },
    },
    {
      name: "invoice_amount",
      label: "Invoice Amount",
      options: { filter: false, display: false },
    },
    {
      name: "payable_amount",
      label: "Payable Amount",
      options: { filter: false, display: false },
    },
    {
      name: "invoice_date",
      label: "Date",
      options: {
        filter: false,
        customBodyRender: (_: any, tableMeta: any) => {
          const row = data[tableMeta.rowIndex];
          return (
            <Stack>
              <Typography variant="body2">
                Invoice Date:{" "}
                <strong>
                  {row.invoice_due_date
                    ? format(new Date(row.invoice_date), "dd/MM/yyyy")
                    : "-"}
                </strong>
              </Typography>
              <Typography variant="body2">
                Due Date:{" "}
                <strong>
                  {row.invoice_due_date
                    ? format(new Date(row.invoice_due_date), "dd/MM/yyyy")
                    : "-"}
                </strong>
              </Typography>
            </Stack>
          );
        },
      },
    },
    {
      name: "invoice_amount",
      label: "Amount",
      options: {
        filter: false,
        customBodyRender: (_: any, tableMeta: any) => {
          const row = data[tableMeta.rowIndex];
          return (
            <Stack>
              <Typography variant="body2">
                Invoice Amount:{" "}
                <strong>₹{amountConverter(row.invoice_amount)}</strong>
              </Typography>
              <Typography variant="body2">
                Payable Amount:{" "}
                <strong>₹{amountConverter(row.payable_amount)}</strong>
              </Typography>
            </Stack>
          );
        },
      },
    },
    {
      name: "invoice_added_at",
      label: "Invoice Added At",
      options: {
        filter: false,
      },
    },
    {
      name: "beneficiary_details.beneficiary_account_name",
      label: "Beneficiary Name",
      options: {
        display: false,
        download: true,
      },
    },
    {
      name: "beneficiary_details.beneficiary_bank_name",
      label: "Beneficiary Bank",
      options: {
        display: false,
        download: true,
      },
    },
    {
      name: "beneficiary_details.beneficiary_account_no",
      label: "Beneficiary Account No",
      options: {
        display: false,
        download: true,
      },
    },
    {
      name: "beneficiary_details.beneficiary_ifsc_code",
      label: "Beneficiary IFSC",
      options: {
        display: false,
        download: true,
      },
    },
    {
      name: "payment_mode",
      label: "Payment Mode",
      options: {
        display: false,
        download: true,
      },
    },
    {
      name: "transaction_id",
      label: "Transaction ID",
      options: {
        display: false,
        download: true,
      },
    },
    {
      name: "paid_at",
      label: "Paid On",
      options: {
        display: false,
        download: true,
      },
    },
    {
      name: "emi_amount",
      label: "EMI Amount",
      options: {
        display: false,
        download: true,
      },
    },
    {
      name: "repayment_tenure",
      label: "EMI Tenure (Days)",
      options: {
        display: false,
        download: true,
      },
    },
    {
      name: "invoice_file_url",
      label: "Invoice URL",
      options: {
        display: false,
        download: true,
      },
    },
    {
      name: "status",
      label: "Status",
      options: {
        filter: true,
        customBodyRender: (value: string) => {
          const colorMap: any = {
            approved: { bg: "#C6F6D5", color: "#22543D" },
            pending: { bg: "#FEEBC8", color: "#7B341E" },
            paid: { bg: "#E6FFFA", color: "#065F46" },
            cancelled: { bg: "#FED7D7", color: "#822727" },
            rejected: { bg: "#FED7D7", color: "#822727" },
          };

          return (
            <Chip
              size="small"
              label={value.toUpperCase()}
              sx={{
                backgroundColor: colorMap[value]?.bg,
                color: colorMap[value]?.color,
                fontWeight: 500,
              }}
            />
          );
        },
      },
    },
    {
      name: "actions",
      label: "Action",
      options: {
        display: filterBy === "under_review",
        download: false,
        sort: false,
        filter: false,
        customBodyRender: (_: any, tableMeta: any) => {
          const row = data[tableMeta.rowIndex];

          return (
            <Box
              textAlign="center"
              sx={{ cursor: "pointer" }}
              onClick={() => {
                setActiveBorrowerId(data[tableMeta.rowIndex]?.borrower_id);
                setActiveInvoiceUrl(data[tableMeta.rowIndex]?.invoice_file_url);
                setAccountNumber(
                  data[tableMeta.rowIndex]?.beneficiary_details
                    ?.beneficiary_account_no,
                );
                setIfscCode(
                  data[tableMeta.rowIndex]?.beneficiary_details
                    ?.beneficiary_ifsc_code,
                );
                setActiveApplicationId(
                  data[tableMeta.rowIndex]?.application_id,
                );
                setPayeeName(data[tableMeta.rowIndex]?.payee_name);
                setPayableAmount(
                  Number(data[tableMeta.rowIndex]?.payable_amount || 0).toFixed(
                    2,
                  ),
                );
                setUpdateApplication(true);
              }}
            >
              <PermIdentityIcon />
              <Typography
                sx={{
                  fontSize: "10px !important",
                  fontWeight: "600",
                  textTransform: "uppercase",
                }}
              >
                Invoice
              </Typography>
            </Box>
          );
        },
      },
    },
  ];

  const CustomToolbar = () => {
    return (
      <FormControl size="small">
        <InputLabel id="status-label">Status</InputLabel>
        <Select
          labelId="status-label"
          name="status"
          id="status"
          label="status"
          value={filterBy}
          onChange={(e) => setFilterBy(e.target.value)}
        >
          <MenuItem value="under_review">Under Review</MenuItem>
          <MenuItem value="approved">Approved</MenuItem>
          <MenuItem value="rejected">Rejected</MenuItem>
        </Select>
      </FormControl>
    );
  };

  const options: any = {
    customToolbar: () => <CustomToolbar />,
    textLabels: {
      body: {
        noMatch: loading ? (
          <CircularProgress sx={{ color: "#0058ff" }} />
        ) : (
          "Sorry, there is no matching data to display"
        ),
      },
    },
    ...commonTableOptions,
    searchPlaceholder: "Invoice ID/Payee Name",
    rowsPerPageOptions: [10, 50, 100, 200],
    filter: false,
    download: [
      "ravi@medpay.in",
      "arun@medpay.in",
      "tarun.yadav@medpay.in",
      "amit@medpay.in",
      "anuj@medpay.in",
      "onboardings@medpay.in",
      "joys.saji@medpay.in",
      "jai.srivastava@medpay.in",
      "anamika.shrivastava@medpay.in",
      "anirudh.agarwal@medpay.in",
      "sneha.jaiswal@medpay.in",
    ].includes(String(user?.user_id))
      ? true
      : false,
    downloadOptions: {
      filename: "Borrower_Invoice_" + new Date().toLocaleDateString(),
      filterOptions: {
        useDisplayedColumnsOnly: false,
        useDisplayedRowsOnly: true,
      },
    },
    rowsPerPage: 10,
    expandableRows: true,
    expandableRowsHeader: false,
    expandableRowsOnClick: true,
    renderExpandableRow: (rowData: any, rowMeta: any) => {
      const row = data[rowMeta.dataIndex];

      const isCancelledOrRejected = ["cancelled", "rejected"].includes(
        row.status?.toLowerCase(),
      );

      return (
        <tr>
          <td colSpan={rowData.length + 1}>
            <Box p={2} bgcolor="#F8F9FC">
              {isCancelledOrRejected ? (
                <Box
                  sx={{
                    border: "1px solid #FCA5A5",
                    backgroundColor: "#FEF2F2",
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: "#B91C1C" }}
                  >
                    {row.status === "cancelled"
                      ? row.cancelled_reason || "Invoice was cancelled"
                      : row.reject_reason || "Invoice was rejected"}
                  </Typography>
                </Box>
              ) : (
                <Stack direction="row" spacing={2}>
                  <Box
                    sx={{
                      flex: 1,
                      backgroundColor: "#fff",
                      border: "1px solid #E5E7EB",
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    <Typography fontWeight={600} mb={1}>
                      Beneficiary Details
                    </Typography>

                    <Stack spacing={0.5}>
                      <Typography variant="body2">
                        Name:{" "}
                        <b>
                          {row.beneficiary_details?.beneficiary_account_name ||
                            "--"}
                        </b>
                      </Typography>
                      <Typography variant="body2">
                        Bank:{" "}
                        {row.beneficiary_details?.beneficiary_bank_name || "--"}
                      </Typography>
                      <Typography variant="body2">
                        Account No:{" "}
                        {row.beneficiary_details?.beneficiary_account_no ||
                          "--"}
                      </Typography>
                      <Typography variant="body2">
                        IFSC:{" "}
                        {row.beneficiary_details?.beneficiary_ifsc_code || "--"}
                      </Typography>
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      backgroundColor: "#fff",
                      border: "1px solid #E5E7EB",
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    <Typography fontWeight={600} mb={1}>
                      Payment Status
                    </Typography>

                    <Stack spacing={0.75}>
                      <Typography variant="body2">
                        Status: <b>{row.status.toUpperCase()}</b>
                      </Typography>
                      <Typography variant="body2">
                        Payment Mode: {row.payment_mode || "--"}
                      </Typography>
                      <Typography variant="body2">
                        Transaction ID: {row.transaction_id || "--"}
                      </Typography>
                      <Typography variant="body2">
                        Paid On:{" "}
                        {row.paid_at
                          ? format(new Date(row.paid_at), "dd/MM/yyyy")
                          : "--"}
                      </Typography>
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      backgroundColor: "#fff",
                      border: "1px solid #E5E7EB",
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    <Typography fontWeight={600} mb={1}>
                      EMI Details
                    </Typography>

                    <Stack spacing={0.75}>
                      <Typography variant="body2">
                        EMI Amount: ₹{amountConverter(row.emi_amount || 0)}
                      </Typography>
                      <Typography variant="body2">
                        Tenure: {row.repayment_tenure || "--"} days
                      </Typography>
                      <Typography variant="body2">
                        EMI Start Date:{" "}
                        {row.emi_start_date
                          ? format(new Date(row.emi_start_date), "dd/MM/yyyy")
                          : "--"}
                      </Typography>
                      <Typography variant="body2">
                        EMI End Date:{" "}
                        {row.emi_end_date
                          ? format(new Date(row.emi_end_date), "dd/MM/yyyy")
                          : "--"}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              )}
            </Box>
          </td>
        </tr>
      );
    },
  };

  return (
    <Box>
      <MUIDataTable title="" data={data} columns={cols} options={options} />
      {updateApplication ? (
        <UpdateApplicationModal
          updateApplication={updateApplication}
          setUpdateApplication={setUpdateApplication}
          activeApplicationId={activeApplicationId}
          invoiceUrl={activeInvoiceUrl}
          accountNumber={accountNumber}
          ifscCode={ifscCode}
          setAccountNumber={setAccountNumber}
          setIfscCode={setIfscCode}
          activeBorrowerId={activeBorrowerId}
          payeeName={payeeName}
          payableAmount={payableAmount}
          callBack={() =>
            setData(
              data?.filter(
                (item: any) => item.borrower_id !== activeBorrowerId,
              ),
            )
          }
        />
      ) : null}
    </Box>
  );
};

export default BorrowerFinancing;
